/**
 * Copyright (C) 2018 Egylis,
 * Siège social, 46 rue René Clair, 75892 PARIS cedex 18 - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 16/11/18.
 *
 * Version : Generator 2.0.9
 */
import {Injectable} from '@angular/core';
import {MoPersistenceEntity, UtilMpaFunction} from './utility';
import {ExprOperator, ExprQuery, FilterOperator, FilterQuery, OrderEnum, Query} from './query';
import {ColumnType} from './defEntity';
import {MpaConstants} from './mpaConstants';
import {MOEntity} from './entity';
import {MOEntityMeta} from './metaEntity';
import {
    Action,
    FileData,
    FileOrigin,
    FileStatus,
    FileType,
    FileUploadStatus,
    Journal,
    MsaService,
    SynchroStreamInfo,
    TableConfig
} from '../msa';
import {StatusEnum} from '../entity';
import {format, isDate, parseISO} from 'date-fns';
import {BehaviorSubject, Observable} from 'rxjs';
import {LogService} from '../log';
import {SynchroStreamTableInfo} from './utilEntity';
import {HttpClient} from '@angular/common/http';
import {OnlineQuery} from './onlineQuery/onlineQuery';
import {OnlineWhereQuery} from './onlineQuery/onlineWhereQuery';
import {OnlineValueType} from './onlineQuery/onlineValueType';
import {OnlineExprQuery} from './onlineQuery/onlineExprQuery';
import {OnlineFilterQuery} from './onlineQuery/onlineFilterQuery';
import {OnlineSave} from './onlineQuery/onlineSave';
import {CordovaBackService} from '../service';
import {OnlineDelete} from './onlineQuery/onlineDelete';

@Injectable()
export class MpaService {
    /**
     * Sql DateTime Format
     */
    private static SQL_DATETIME_FORMAT: string = 'yyyy-MM-dd HH:mm:ss';
    /**
     * Sql Date Format
     */
    private static SQL_DATE_FORMAT: string = 'yyyy-MM-dd';
    /**
     * Library Version
     */
    private _libraryVersion: string = '2.0.9';
    /**
     * Data Version
     */
    private _dataVersion: string = '1.0.1';
    /**
     * Library Date
     */
    private _libraryDate: string = '2022-02-28T15:48:22.706Z';
    /**
     * Dexie Database
     */
    // private dexieDb: Dexie;
    /**
     * Entities.
     */
    private entities: Array<MoPersistenceEntity<MOEntity>> = [];
    /**
     * Table Name to entity
     */
    private tableName2entity: { [key: string]: MoPersistenceEntity<MOEntity>; };
    /**
     * Is started
     */
    private _isStarted: boolean = false;
    /**
     * Event Start Sync
     */
    private eventStartSyncFunction: () => Promise<boolean>;
    /**
     * Event End Sync
     */
    private eventEndSyncFunction: () => Promise<boolean>;
    /**
     * Connectivity Quality Sync
     */
    private connectivityQualityFunction: () => Promise<boolean>;
    /**
     * Connectivity Sync
     */
    private connectivityFunction: () => Promise<boolean>;
    /**
     * On Resume Stream.
     */
    private tableInfoStream: BehaviorSubject<SynchroStreamTableInfo>;
    /**
     * On Resume Stream.
     */
    private infoStream: BehaviorSubject<SynchroStreamInfo>;
    /**
     * Index.
     */
    private index: number = 0;
    /**
     * Server Url
     */
    private serverUrl: string | null = null;
    /**
     * Sync Api Key
     */
    private syncKey: string;

    /**
     * Constructor
     */
    constructor(private msaService: MsaService,
                private cordovaBackService: CordovaBackService,
                private http: HttpClient,
                private logService: LogService) {
        this.tableInfoStream = new BehaviorSubject<SynchroStreamTableInfo>(null);
        this.infoStream = new BehaviorSubject<SynchroStreamInfo>(null);
        this.initData();
    }

    /**
     * Create the object
     * @param succ succ
     * @param query query
     * @param persistenceEntity persistence entity.
     */
    private static createObject<T extends MOEntity>(succ: any[], query: Query<T>,
                                                    persistenceEntity: MoPersistenceEntity<MOEntity>): Array<T> {
        const res: Array<T> = [];
        for (const itemDB of succ) {
            const itemObj: T = new query.queryClass.classEntity();
            res.push(itemObj);
            for (const column of persistenceEntity.columns) {
                if (itemDB[column.name] != null) {
                    switch (column.type) {
                        case ColumnType.DATETIME:
                        case ColumnType.DATE:
                            if (typeof itemDB[column.name] === 'string') {
                                if (itemDB[column.name].trim().length > 0) {
                                    // @ts-ignore
                                    itemObj[column.propertyName] = parseISO(itemDB[column.name]);
                                }
                            } else {
                                if (typeof itemDB[column.name] === 'number') {
                                    // @ts-ignore
                                    itemObj[column.propertyName] = new Date(itemDB[column.name]);
                                } else {
                                    // @ts-ignore
                                    itemObj[column.propertyName] = itemDB[column.name];
                                }
                            }
                            break;
                        default:
                            // @ts-ignore
                            itemObj[column.propertyName] = itemDB[column.name];
                            break;
                    }
                }
            }
        }
        return res;
    }

    /**
     * Create the Dexie schema from a entity
     * @param entity persistence entity
     */
    // private static computeSchema(entity: MoPersistenceEntity<MOEntity>): string {
    //     const columnNames: Array<string> = [];
    //     for (const column of entity.columns) {
    //         if (column.primaryKey) {
    //             columnNames.unshift(column.name);
    //         } else {
    //             if (column.index) {
    //                 columnNames.push(column.name);
    //             }
    //         }
    //     }
    //     return columnNames.join(',');
    // }


    /**
     * createCanonicDSyncData
     * @param {T} item
     * @param {MoPersistenceEntity<MOEntity>} persistenceEntity
     * @returns {{}}
     */
    private static createCanonicDSyncData<T extends MOEntity>(item: T, persistenceEntity: MoPersistenceEntity<MOEntity>): {} {
        const data = {};
        for (const column of persistenceEntity.columns) {
            // @ts-ignore
            if (item[column.propertyName] != null) {
                switch (column.type) {
                    case ColumnType.DATETIME:
                        // @ts-ignore
                        if (typeof item[column.propertyName] === 'string') {
                            // @ts-ignore
                            if (item[column.propertyName].trim().length > 0) {
                                // @ts-ignore
                                data[column.name] = item[column.propertyName];
                            }
                        } else {
                            // @ts-ignore
                            if (typeof item[column.propertyName] === 'number') {
                                // @ts-ignore
                                data[column.name] = format(new Date(item[column.propertyName]), MpaService.SQL_DATETIME_FORMAT);
                            } else {
                                // @ts-ignore
                                if (isDate(item[column.propertyName])) {
                                    // @ts-ignore
                                    data[column.name] = format(item[column.propertyName], MpaService.SQL_DATETIME_FORMAT);
                                } else {
                                    // @ts-ignore
                                    data[column.name] = item[column.propertyName];
                                }
                            }
                        }
                        break;
                    case ColumnType.DATE:
                        // @ts-ignore
                        if (typeof item[column.propertyName] === 'string') {
                            // @ts-ignore
                            if (item[column.propertyName].trim().length > 0) {
                                // @ts-ignore
                                data[column.name] = item[column.propertyName];
                            }
                        } else {
                            // @ts-ignore
                            if (typeof item[column.propertyName] === 'number') {
                                // @ts-ignore
                                data[column.name] = format(new Date(item[column.propertyName]), MpaService.SQL_DATE_FORMAT);
                            } else {
                                // @ts-ignore
                                if (isDate(item[column.propertyName])) {
                                    // @ts-ignore
                                    data[column.name] = format(item[column.propertyName], MpaService.SQL_DATE_FORMAT);
                                } else {
                                    // @ts-ignore
                                    data[column.name] = item[column.propertyName];
                                }
                            }
                        }
                        break;
                    default:
                        // @ts-ignore
                        data[column.name] = item[column.propertyName];
                        break;
                }
            }
        }
        return data;
    }


    /**
     * Do Filter
     * @param item item
     * @param filter filter
     */
    private static doFilter<T extends MOEntity>(item: {}, filter: FilterQuery): boolean {
        // TODO gérer les cas bizarre (non string)
        let res: boolean = false;
        if (filter != null && filter.operator != null) {
            switch (filter.operator) {
                case FilterOperator.EQUAL:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name] === filter.value;
                    }
                    break;
                case FilterOperator.CONTAIN:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name].includes(filter.value);
                    }
                    break;
                case FilterOperator.START_WITH:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name].startsWith(filter.value);
                    }
                    break;
                case FilterOperator.END_WITH:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name].endsWith(filter.value);
                    }
                    break;
                case FilterOperator.GREATHER:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name] > filter.value;
                    }
                    break;
                case FilterOperator.GREATHER_OR_EQUAL:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name] >= filter.value;
                    }
                    break;
                case FilterOperator.LESS:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name] < filter.value;
                    }
                    break;
                case FilterOperator.LESS_OR_EQUAL:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        // @ts-ignore
                        res = item[filter.column.name] <= filter.value;
                    }
                    break;
                case FilterOperator.IN:
                    // @ts-ignore
                    if (item[filter.column.name] != null) {
                        const values: Array<string> = filter.value as Array<string>;
                        // @ts-ignore
                        res = values.includes(item[filter.column.name]);
                    }
                    break;
                case FilterOperator.NULL:
                    // @ts-ignore
                    res = item[filter.column.name] == null;
                    break;
                case FilterOperator.NOT_NULL:
                    // @ts-ignore
                    res = item[filter.column.name] != null;
                    break;
                case FilterOperator.NULL_OR_EMPTY:
                    // @ts-ignore
                    res = item[filter.column.name] == null || item[filter.column.name] === '';
                    break;
                case FilterOperator.EMPTY:
                    // @ts-ignore
                    res = item[filter.column.name] === '';
                    break;
                case FilterOperator.NOT_EMPTY:
                    // @ts-ignore
                    res = item[filter.column.name] !== '';
                    break;
                case FilterOperator.NOT_NULL_AND_NOT_EMPTY:
                    // @ts-ignore
                    res = item[filter.column.name] != null && item[filter.column.name] !== '';
                    break;
            }
        }
        return res;
    }


    /**
     * Init Data
     */
    private initData() {
        for (const entityClass of MpaConstants.META_ENTITY_CLASSES) {
            this.entities.push(new MoPersistenceEntity(entityClass));
        }
        this.tableName2entity = {};
        // const schema: { [key: string]: string; } = {};
        for (const entity of this.entities) {
            this.tableName2entity[entity.table] = entity;
            // schema[entity.table] = MpaService.computeSchema(entity);
        }
        // this.dexieDb = new Dexie(environment.name + '_' + MpaConstants.DEXIE_NAME + '_' + environment.mode);
        //
        // for (let i = 1; i < MpaConstants.DEXIE_VERSION; i++) {
        //     this.dexieDb.version(i).stores(
        //         MpaHistory.SCHEMA_HISTORY[i]
        //     ).upgrade(MpaHistory.SCHEMA_UPGRADE[i]);
        // }
        // this.dexieDb.version(MpaConstants.DEXIE_VERSION).stores(
        //     schema
        // ).upgrade(MpaHistory.SCHEMA_UPGRADE[MpaConstants.DEXIE_VERSION]);
        // this.msaService.initDB(this.dexieDb);

        this.eventStartSyncFunction = this.defaultEventFunction.bind(this);
        this.eventEndSyncFunction = this.defaultEventFunction.bind(this);
        this.connectivityQualityFunction = this.defaultEventFunction.bind(this);
        this.connectivityFunction = this.defaultEventFunction.bind(this);

        this._isStarted = true;
    }

    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStart(): Promise<StatusEnum> {
        this.logService.logLog('=========== MpaService isReadyToStart', 'MpaService', 'isReadyToStart', 3);
        return new Promise(
            (resolve, reject) => {
                resolve(StatusEnum.OK);
            });
    }

    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeout(promiseResolve: (r: StatusEnum) => void): void {
        this.logService.logLog('-- MpaService isStartedTimeout', 'MpaService', 'isStartedTimeout', 3);
        if (this._isStarted) {
            this.logService.logLog('-- MpaService isStartedTimeout -- OK', 'MpaService', 'isStartedTimeout', 3);
            promiseResolve(StatusEnum.OK);
        } else {
            this.logService.logLog('-- MpaService isStartedTimeout -- TimeOut', 'MpaService', 'isStartedTimeout', 3);
            setTimeout(this.isStartedTimeout.bind(this, promiseResolve), 100);
        }
    }


    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStartToQueryOnline(): Promise<StatusEnum> {
        this.logService.logLog('=========== MpaService isReadyToStartToQueryOnline', 'MpaService', 'isReadyToStart', 3);
        return new Promise(
            (resolve, reject) => {
                if (this.serverUrl != null) {
                    resolve(StatusEnum.OK);
                } else {
                    this.isStartedTimeoutToQueryOnline(resolve);
                }
            });
    }

    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeoutToQueryOnline(promiseResolve: (r: StatusEnum) => void): void {
        this.logService.logLog('-- MpaService isStartedTimeoutToQueryOnline', 'MpaService', 'isStartedTimeout', 3);
        if (this.serverUrl != null) {
            this.logService.logLog('-- MpaService isStartedTimeoutToQueryOnline -- OK', 'MpaService', 'isStartedTimeout', 3);
            promiseResolve(StatusEnum.OK);
        } else {
            this.logService.logLog('-- MpaService isStartedTimeoutToQueryOnline -- TimeOut', 'MpaService', 'isStartedTimeout', 3);
            setTimeout(this.isStartedTimeoutToQueryOnline.bind(this, promiseResolve), 100);
        }
    }


    /**
     * Init Synchro
     * @param syncServerUrl syncServerUrl
     * @param syncApiKey syncApiKey
     * @param syncInterval syncApiKey
     * @param startSyncTimerMode startSyncTimerMode
     * @param syncLimit syncLimit
     */
    public initSynchro(
        syncServerUrl: string,
        syncApiKey: string,
        syncInterval: number,
        startSyncTimerMode: boolean,
        syncLimit: number
    ): void {
        // this.msaService.setServerUrl(syncServerUrl);
        // this.msaService.setSyncKey(syncApiKey);
        this.syncKey = syncApiKey;
        this.serverUrl = syncServerUrl.replace(
            'synchronization/synchronize/V1',
            'geo/rest/onlineQuery/V1'
        );
        // this.msaService.setSyncInterval(syncInterval);
        //
        // for (const entityClass of MpaConstants.META_ENTITY_CLASSES) {
        //     if (entityClass.syncDataServerToClient) {
        //         this.msaService.addTableToSync(entityClass.tableName);
        //     } else {
        //         this.msaService.removeTableToSync(entityClass.tableName);
        //     }
        // }
        // this.msaService.setEventItemSync(this.eventItemSync.bind(this));
        // this.msaService.setEventItemsSync(this.eventItemsSync.bind(this));
        // this.msaService.setEventStartSync(this.eventStartSyncFunction);
        // this.msaService.setEventEndSync(this.eventEndSyncFunction);
        // // this.msaService.setEventConnectivity(this.connectivityFunction);
        // this.msaService.setEventConnectivityQuality(this.connectivityQualityFunction);
        // if (startSyncTimerMode && this.msaService.isSyncTimerMode) {
        //     this.msaService.startSyncTimerMode();
        // }
    }

    /**
     * Init Start Synchro Auto if needed
     */
    public initStartSynchro(): void {
        // if (this.msaService.isSyncTimerMode) {
        //     this.msaService.startSyncTimerMode();
        // }
    }

    /**
     * Add a synchonization filter.
     * @param {MOEntityMeta<MOEntity>} table
     * @param {Array<FilterQuery>} filterQueries : only EQUAL
     * @returns {boolean}
     */
    public addFilterSync(
        table: MOEntityMeta<MOEntity>,
        filterQueries: Array<FilterQuery>
    ): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                resolve(true);
            }
        );
    }

    /**
     * Find Persistence Entity from an object
     * @param item object to find Entity
     */
    private findEntityByObject(item: MOEntity): MoPersistenceEntity<MOEntity> {
        let response: MoPersistenceEntity<MOEntity> = null;
        for (const entityClass of MpaConstants.META_ENTITY_CLASSES) {
            if (item instanceof entityClass.classEntity) {
                response = this.tableName2entity[entityClass.tableName];
            }
        }
        return response;
    }


    /**
     * Find Persistence Entity from a class
     * @param classEntity object to find Entity
     */
    private findEntityByMetaClass<T extends MOEntity>(classEntity: MOEntityMeta<T>): MoPersistenceEntity<MOEntity> {
        return this.tableName2entity[classEntity.tableName];
    }


    /**
     * Save an entity
     * @param item entity to save
     * @return the key
     */
    public save<T extends MOEntity>(item: T): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                item.status = 2;
                let action: Action = Action.UPDATE;
                item.timeStamp = UtilMpaFunction.getTimeStampMadeInEgylis();
                if (item.uuid == null) {
                    item.uuid = UtilMpaFunction.uuid();
                }
                if (item.__isNew) {
                    item.status = 3;
                    action = Action.INSERT;
                    item.__isNew = false;
                }
                let data: {} = null;
                const persistenceEntity = this.findEntityByObject(item);
                if (persistenceEntity != null) {
                    data = MpaService.createCanonicDSyncData(item, persistenceEntity);
                    if (data != null) {
                        // @ts-ignore
                        delete data['___previousServerId'];
                        // @ts-ignore
                        delete data['___isNew'];
                        const onlineSave: OnlineSave = {
                            apiKey: this.syncKey,
                            tableName: persistenceEntity.table,
                            deviceId: this.cordovaBackService.getDeviceId(),
                            new: item.__isNew,
                            data
                        };
                        this.http.post<Array<any>>(this.serverUrl + '/saveData', onlineSave)
                            .subscribe(
                                journals => {
                                    resolve('OK');
                                },
                                () => {
                                    // TODO Gérer le cas d'erreur
                                    reject();
                                }
                            );
                    } else {
                        // TODO Gérer le cas d'erreur
                        reject();
                    }
                } else {
                    // TODO Gérer le cas d'erreur
                    reject();
                }
            }
        );
    }


    /**
     * Delete an entity
     * @param item entity to delete
     * @return the key
     */
    public delete<T extends MOEntity>(item: T): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                item.status = 1;
                item.timeStamp = UtilMpaFunction.getTimeStampMadeInEgylis();

                const persistenceEntity = this.findEntityByObject(item);
                if (persistenceEntity != null) {
                    const onlineDelete: OnlineDelete = {
                        apiKey: this.syncKey,
                        tableName: persistenceEntity.table,
                        deviceId: this.cordovaBackService.getDeviceId(),
                        uuid: item.uuid
                    };
                    this.http.post<Array<any>>(this.serverUrl + '/deleteData', onlineDelete)
                        .subscribe(
                            journals => {
                                resolve('OK');
                            },
                            () => {
                                // TODO Gérer le cas d'erreur
                                reject();
                            }
                        );
                } else {
                    // TODO Gérer le cas d'erreur
                    reject();
                }
            }
        );
    }


    /**
     * Save  entities (NO TRANSACTION)
     * @param items entities to save
     * @return the promise
     */
    public saveAll<T extends MOEntity>(items: Array<T>): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                const item = items.shift();
                if (item != null) {
                    this.save(item).then(
                        () => {
                            this.saveAll(items).then(
                                (val) => resolve(val),
                                () => {
                                    // TODO Gérer le cas d'erreur
                                    reject();
                                }
                            );
                        },
                        () => {
                            // TODO Gérer le cas d'erreur
                            reject();
                        }
                    );
                } else {
                    resolve(true);
                }
            }
        );
    }


    /**
     * Delete entities (NO TRANSACTION)
     * @param items entities to delete
     * @return the promise
     */
    public deleteAll<T extends MOEntity>(items: Array<T>): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                const item = items.shift();
                if (item != null) {
                    this.delete(item).then(
                        () => {
                            this.deleteAll(items).then(
                                (val) => resolve(val),
                                () => {
                                    // TODO Gérer le cas d'erreur
                                    reject();
                                }
                            );
                        },
                        () => {
                            // TODO Gérer le cas d'erreur
                            reject();
                        }
                    );
                } else {
                    resolve(true);
                }
            }
        );
    }


    /**
     * Execute the query
     * @param query the query to execute
     */
    public executeQuery<T extends MOEntity>(query: Query<T>): Promise<Array<T>> {
        console.log('###########################"');
        console.log(query);
        console.log('###########################"');
        return new Promise(
            (resolve, reject) => {
                this.isReadyToStartToQueryOnline().then(
                    () => {
                        const persistenceEntity = this.findEntityByMetaClass(query.queryClass);
                        if (persistenceEntity != null) {
                            const onlineQuery: OnlineQuery = {
                                apiKey: this.syncKey,
                                tableName: query.queryClass.tableName,
                                limit: query.limit,
                                offset: query.offset,
                                indexSort: null,
                                orderSort: OrderEnum.ASC,
                                where: null,
                                expression: null
                            };
                            if (query.indexSort != null) {
                                onlineQuery.indexSort = query.indexSort.name;
                            }
                            if (query.orderSort != null) {
                                onlineQuery.orderSort = query.orderSort;
                            }
                            if (query.where != null && query.where.index != null) {
                                const where: OnlineWhereQuery = {
                                    index: query.where.index.name,
                                    operator: query.where.operator,
                                    type: OnlineValueType.STRING,
                                    arrayStringValue: null,
                                    stringValue: null,
                                    numberValue: null,
                                    booleanValue: null,
                                };
                                if (Array.isArray(query.where.value)) {
                                    where.type = OnlineValueType.ARRAY_STRING;
                                    where.arrayStringValue = query.where.value;
                                } else {
                                    if (typeof (query.where.value) === 'number') {
                                        where.type = OnlineValueType.NUMBER;
                                        where.numberValue = query.where.value;
                                    } else {
                                        if (typeof (query.where.value) === 'boolean') {
                                            where.type = OnlineValueType.BOOLEAN;
                                            where.booleanValue = query.where.value;
                                        } else {
                                            where.type = OnlineValueType.STRING;
                                            where.stringValue = query.where.value + '';
                                        }
                                    }
                                }
                                onlineQuery.where = where;
                            }

                            if (query.expression != null) {
                                onlineQuery.expression = this.convertExpression(query.expression);
                            }

                            this.http.post<Array<any>>(this.serverUrl + '/query', onlineQuery)
                                .subscribe(
                                    journals => {
                                        const res = MpaService.createObject(journals, query, persistenceEntity);
                                        resolve(res);
                                    },
                                    () => {
                                        reject();
                                    }
                                );
                        } else {
                            reject();
                        }
                    },
                    () => {
                        reject();
                    }
                );
            }
        );
    }

    /**
     * Convert expression
     * @param {ExprQuery | null} expr
     * @returns {OnlineExprQuery | null}
     * @private
     */
    private convertExpression(expr: ExprQuery | null): OnlineExprQuery | null {
        let res: OnlineExprQuery | null = null;
        if (expr != null) {
            res = {
                leftExpr: this.convertExpression(expr.leftExpr),
                rightExpr: this.convertExpression(expr.rightExpr),
                filter: this.convertFilter(expr.filter),
                operator: expr.operator
            };
        }
        return res;
    }

    /**
     * Convert Filter
     * @param {FilterQuery | null} expr
     * @returns {OnlineFilterQuery | null}
     * @private
     */
    private convertFilter(expr: FilterQuery | null): OnlineFilterQuery | null {
        let res: OnlineFilterQuery | null = null;
        if (expr != null) {
            res = {
                columnName: expr.column.name,
                operator: expr.operator,
                type: OnlineValueType.STRING,
                arrayStringValue: null,
                stringValue: null,
                numberValue: null,
                booleanValue: null,
            };
            if (Array.isArray(expr.value)) {
                res.type = OnlineValueType.ARRAY_STRING;
                res.arrayStringValue = expr.value;
            } else {
                if (typeof (expr.value) === 'number') {
                    res.type = OnlineValueType.NUMBER;
                    res.numberValue = expr.value;
                } else {
                    if (typeof (expr.value) === 'boolean') {
                        res.type = OnlineValueType.BOOLEAN;
                        res.booleanValue = expr.value;
                    } else {
                        res.type = OnlineValueType.STRING;
                        res.stringValue = expr.value + '';
                    }
                }
            }
        }
        return res;
    }


    /**
     * Do Expression
     * @param item  item
     * @param expr  expression
     */
    private doExpression<T extends MOEntity>(item: {}, expr: ExprQuery): boolean {
        let res: boolean = false;
        if (expr != null && expr.operator != null) {
            switch (expr.operator) {
                case ExprOperator.TRUE:
                    res = true;
                    break;
                case ExprOperator.FALSE:
                    res = false;
                    break;
                case ExprOperator.AND:
                    res = this.doExpression(item, expr.leftExpr);
                    if (res) {
                        res = this.doExpression(item, expr.rightExpr);
                    }
                    break;
                case ExprOperator.OR:
                    res = this.doExpression(item, expr.leftExpr);
                    if (!res) {
                        res = this.doExpression(item, expr.rightExpr);
                    }
                    break;
                case ExprOperator.VAL:
                    res = MpaService.doFilter(item, expr.filter);
                    break;
                case ExprOperator.NOT:
                    res = !MpaService.doFilter(item, expr.filter);
                    break;
            }
        }
        return res;
    }


    /**
     * Reset the content of the table. (**WARNING**, it's a local deletion : this deletion is not sync with the server)
     * @param {Array<MOEntityMeta<MOEntity>>} tables
     */
    public resetTables(tables: Array<MOEntityMeta<MOEntity>>): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                resolve(true);
            }
        );
    }

    /**
     * Get the library modele
     */
    public get libraryVersion(): string {
        return this._libraryVersion;
    }

    /**
     * Get the data Version
     */
    public get dataVersion(): string {
        return this._dataVersion;
    }

    /**
     * Get the library Date
     */
    public get libraryDate(): string {
        return this._libraryDate;
    }

    /**
     * Event Function for Item Sync.
     * @param journal
     */
    // TODO à optimiser avec une transaction sur une même table
    public eventItemSync(journal: Journal): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                reject();
            });
    }

    /**
     * Event Function for Items Sync.
     * @param journals  journals to add
     * @param tableConfig table Config
     */
    public eventItemsSync(journals: Array<Journal>, tableConfig: TableConfig): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                reject();
            });
    }


    private eventItemsSyncBatch(journals: Array<Journal>): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                const journal = journals.shift();
                if (journal != null) {
                    this.eventItemSync(journal).then(
                        () => {
                            this.eventItemsSyncBatch(journals).then(
                                () => {
                                    resolve(true);
                                },
                                () => {
                                    reject();
                                }
                            );
                        },
                        () => {
                            reject();
                        }
                    );
                } else {
                    resolve(true);
                }
            });
    }

    /**
     * Event Function for Item Sync.
     */
    public defaultEventFunction(): Promise<boolean> {
        return new Promise(
            (resolve) => {
                resolve(true);
            });
    }


    /**
     * Clean caches, tempory files...  for a new page.
     */
    public cleanNewPage(): void {
        this.msaService.clearBlobUrlCache();
    }


    /**
     * Get File
     * @param {string} path
     * @param {boolean} local get the file in the device
     * @param {boolean} online get the file in the web
     * @param {boolean} needToDownloadIfNotLocal if the file is found not local, on le met dans la synchro pour
     * la prochaine fois
     * @returns {Promise<FileData>}
     */
    public getFile(path: string, local: boolean, online: boolean, needToDownloadIfNotLocal: boolean): Promise<FileData> {
        return new Promise(
            (resolve, reject) => {
                this.isReadyToStartToQueryOnline().then(
                    () => {
                        // TODO il faut faire la partie des object non sauvegardé
                        const fileData: FileData = {
                            path,
                            file_status: FileStatus.ONLINE,
                            file_origin: FileOrigin.SERVER,
                            file_upload_status: FileUploadStatus.NONE,
                            size: -1,
                            hash: '42',
                            uri: this.serverUrl + '/file/' + this.syncKey + '/' + path
                        };
                        resolve(fileData);
                    },
                    () => {
                        reject();
                    }
                );
            }
        );
    }

    /**
     * Save a file
     * @param fileData  file Data.
     * @param tableName tableName
     * @param syncBoth sync Way
     */
    public saveFile(fileData: FileData, tableName: string, syncBoth: boolean): Promise<FileData> {
        return new Promise(
            (resolve, reject) => {
                switch (fileData.file_type) {
                    case FileType.FILE:
                        const params: any = {
                            filePath:  fileData.path,
                            apiKey:  this.syncKey,
                            deviceId:  this.cordovaBackService.getDeviceId(),
                        };
                        this.cordovaBackService.uploadFileV3(fileData, this.serverUrl + '/saveFile', params).then(
                            _ => {
                                fileData.file_status = FileStatus.ONLINE;
                                fileData.file_origin = FileOrigin.SERVER;
                                fileData.file_upload_status = FileUploadStatus.SAVED;
                                fileData.uri = this.serverUrl + '/file/' + this.syncKey + '/' + fileData.path;
                                resolve(fileData);
                            },
                            () => {
                                // TODO Gérer le cas d'erreur
                                reject();
                            }
                        );
                        break;
                    case FileType.BLOB:
                        const formData = new FormData();
                        formData.append('file', fileData.blob, fileData.path);
                        formData.append('filePath', fileData.path);
                        formData.append('apiKey', this.syncKey);
                        formData.append('deviceId', this.cordovaBackService.getDeviceId());
                        this.http.post(this.serverUrl + '/saveFile',
                            formData,
                            {
                                observe: 'events' as 'events',
                                responseType: 'json' as 'json',
                                reportProgress: true
                            }).subscribe(
                            _ => {
                                fileData.file_status = FileStatus.ONLINE;
                                fileData.file_origin = FileOrigin.SERVER;
                                fileData.file_upload_status = FileUploadStatus.SAVED;
                                fileData.uri = this.serverUrl + '/file/' + this.syncKey + '/' + fileData.path;
                                resolve(fileData);
                            },
                            () => {
                                // TODO Gérer le cas d'erreur
                                reject();
                            }
                        );
                        break;
                }
            }
        );
    }

    /**
     * Comptue the uri for FileData
     * @param fileData
     */
    public computeUriForFileData(fileData: FileData): void {
        this.msaService.computeUriForFileData(fileData);
    }


    /**
     * Stop the current Synchro
     */
    public stopSynchro(): void {
        // this.msaService.stopSynchronization();
    }

    /**
     * Start the current Synchro
     */
    public startSynchro(): void {
        // this.msaService.startSynchronization();
    }

    /**
     * Is syncing ?
     */
    public isSyncing(): boolean {
        return false;
        // return this.msaService.isSyncing;
    }

    /**
     * Is an auto sync
     */
    public isAutoSync(): boolean {
        return false;
        // return this.msaService.isSyncTimerMode;
    }

    /**
     * Start autoSync
     */
    public startAutoSync(): void {
        // this.msaService.startSyncTimerMode();
    }

    /**
     * Stop autoSync
     */
    public stopAutoSync(): void {
        // this.msaService.stopSyncTimerMode();
    }

    /**
     * Get the observable of the Info stream.
     * @return {Observable<SynchroStreamInfo>}
     */
    public getInfoStream(): Observable<SynchroStreamInfo> {
        return this.infoStream.asObservable();
    }

    /**
     * Get the observable of the Table Info stream.
     * @return {Observable<SynchroStreamInfo>}
     */
    public getTableInfoStream(): Observable<SynchroStreamTableInfo> {
        return this.tableInfoStream.asObservable();
    }

    /**
     * Is Sync Table : est-ce les tables ont été synchronisées ?
     * @param metaEntities
     */
    public isSyncTableOnTime(metaEntities: Array<MOEntityMeta<MOEntity>>): boolean {
        return true;
    }


}
